"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Loader2, ShieldCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function MemberRegistrationForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="91105" id="contact-form" className="bg-background text-foreground relative min-h-screen flex items-center justify-center py-24 px-4 overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1609030/img/nicotine_weave_texture.png)] opacity-5 bg-cover bg-center mix-blend-multiply pointer-events-none" aria-hidden="true" />

      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-24 bg-gradient-to-b from-transparent to-primary/20" />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-px h-24 bg-gradient-to-t from-transparent to-primary/20" />

      <div ref={ref} className={`relative z-10 w-full max-w-xl transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <Card className="bg-card/90 backdrop-blur-md border-border shadow-xl rounded-3xl overflow-hidden relative">
          {}
          <div className="absolute top-0 left-0 w-4 h-4 border-t border-l border-accent/40" />
          <div className="absolute top-0 right-0 w-4 h-4 border-t border-r border-accent/40" />
          <div className="absolute bottom-0 left-0 w-4 h-4 border-b border-l border-accent/40" />
          <div className="absolute bottom-0 right-0 w-4 h-4 border-b border-r border-accent/40" />

          <CardHeader className="text-center pb-8 pt-10 border-b border-border/50">
            <div className="mx-auto w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mb-4 border border-accent/20 shadow-sm !bg-[url(/img/BODYWERKS-LOGO-COLOR-NO-BGR-scaled.png)] !bg-[url(/img/cropped-Logo-Silhoute-HIGH-REZ.png)] !bg-[url(/img/BODYWERKS-LOGO-COLOR-NO-BGR-scaled.png)] !bg-[url(/img/Logo-Silhoute-HIGH-REZ.png)]">
              <ShieldCheck className="w-6 h-6 text-accent" />
            </div>
            <CardTitle className="font-serif text-3xl font-bold tracking-tight text-primary uppercase">Contact Us</CardTitle>
            <CardDescription className="font-sans text-muted-foreground mt-2">Please feel free to send us a note with your questions:</CardDescription>
          </CardHeader>

          <CardContent className="pt-8 pb-10 px-6 sm:px-10">
            <FormProvider formKey="contact_form_bookanappointmentbodywerkschiropracticlimassolnicosia" formId="contact_form_bookanappointmentbodywerkschiropracticlimassolnicosia" sectionName="Contact Form" defaultValues={{
            name: "",
            surname: "",
            email: "",
            phone: "",
            comment: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2,
              maxLength: 30
            },
            surname: {
              required: true,
              minLength: 2,
              maxLength: 30
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: false
            },
            comment: {
              required: false
            }
          }} submitText="Send Message" successMessage="Thank you for your message. Our team will contact you shortly to assist with your inquiry." errorMessage="Submission failed. Please check your information and try again.">
              <RegistrationFormInner />
            </FormProvider>
          </CardContent>
        </Card>
      </div>
    </section>;
}
function RegistrationFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center border border-accent shadow-md">
          <ShieldCheck className="w-8 h-8 text-accent" />
        </div>
        <div className="space-y-2">
          <h3 className="font-serif text-xl font-bold text-primary uppercase tracking-wider">Message Sent</h3>
          <p className="font-sans text-muted-foreground">{successMessage}</p>
        </div>
        <Button variant="outline" className="font-sans mt-4 border-accent/50 hover:bg-accent/5" asChild>
          <Link to="/HomePage">Return to Home</Link>
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Minimum 2 characters"
        },
        maxLength: {
          value: 30,
          message: "Maximum 30 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans uppercase tracking-widest text-[10px] text-muted-foreground font-semibold">Name</FormLabel>
              <FormControl>
                <Input placeholder="First name" className="font-sans bg-background border-border focus-visible:border-accent focus-visible:ring-accent/10 rounded-md" {...field} />
              </FormControl>
              <FormMessage className="font-sans text-xs" />
            </FormItem>} />

        <FormField control={control} name="surname" rules={{
        required: "Surname is required",
        minLength: {
          value: 2,
          message: "Minimum 2 characters"
        },
        maxLength: {
          value: 30,
          message: "Maximum 30 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans uppercase tracking-widest text-[10px] text-muted-foreground font-semibold">Surname</FormLabel>
              <FormControl>
                <Input placeholder="Last name" className="font-sans bg-background border-border focus-visible:border-accent focus-visible:ring-accent/10 rounded-md" {...field} />
              </FormControl>
              <FormMessage className="font-sans text-xs" />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email address is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email format"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans uppercase tracking-widest text-[10px] text-muted-foreground font-semibold">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="your@email.com" className="font-sans bg-background border-border focus-visible:border-accent focus-visible:ring-accent/10 rounded-md" {...field} />
            </FormControl>
            <FormMessage className="font-sans text-xs" />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans uppercase tracking-widest text-[10px] text-muted-foreground font-semibold">Phone Number</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="Your mobile, e.g. +357 9x xxx xxx" className="font-sans bg-background border-border focus-visible:border-accent focus-visible:ring-accent/10 rounded-md" {...field} />
            </FormControl>
            <FormDescription className="font-sans text-[10px] text-accent/70">
              Optional: For faster coordination.
            </FormDescription>
            <FormMessage className="font-sans text-xs" />
          </FormItem>} />

      <FormField control={control} name="comment" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans uppercase tracking-widest text-[10px] text-muted-foreground font-semibold">Your Message</FormLabel>
            <FormControl>
              <textarea className="file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex min-h-[120px] w-full min-w-0 rounded-md border bg-transparent px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive font-sans" placeholder="How can we help you today?" {...field} />
            </FormControl>
            <FormMessage className="font-sans text-xs" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-md flex items-start gap-3">
          <div className="w-1 h-full bg-destructive rounded-full" />
          <p className="text-destructive font-sans text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-sans uppercase tracking-widest font-bold mt-8 transition-all hover:shadow-lg">
        {isSubmitting ? <>
            <Loader2 className="animate-spin mr-2 w-5 h-5" />
            Sending...
          </> : "Send Message"}
      </Button>
    </Form>;
}